import { formatISO } from 'date-fns/formatISO';
import { getYear } from 'date-fns/getYear';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import type { FormikContextType, FormikHelpers } from 'formik';
import type { BookMeetingFormState, BookMeetingFormTranslations, TimeSlot } from '../Types/types';
import type { BookMeetingRequest } from '~/Features/Api/BookMeeting/Models/Dto/BookMeetingRequest.csharp';
import { combineValidators, isEmail, isNotEmptyString, isNotNull, isTrue } from '~/Partials/FormFields/validators';
import { makePostRequest } from '~/Partials/Utils/HttpRequest/makePostRequest';

export const getTimeSlot = (selectedDate: Date | null, timeSlots: TimeSlot[]): { [key: string]: string } => {
    if (!selectedDate) {
        return {};
    }

    const reduceSlots = (acc: Record<string, string>, curr: string) => ({ ...acc, ...{ [curr]: curr } });

    const year = getYear(selectedDate);

    const findSlots = (slot: TimeSlot) =>
        isWithinInterval(selectedDate, {
            start: new Date(year, slot.from.month - 1, slot.from.day),
            end: new Date(year, slot.to.month - 1, slot.to.day),
        });

    const availableSlots = timeSlots.filter(findSlots).flatMap(slot => slot.slots);

    return availableSlots.reduce(reduceSlots, {});
};

export const updateTimeSlotsFieldOnDatePick = (
    formik: FormikContextType<BookMeetingFormState>,
    values: BookMeetingFormState
): boolean => {
    const { values: updateValues, touched } = formik;
    const shouldUpdate =
        updateValues.date !== values.date ||
        updateValues.timeSlot !== values.timeSlot ||
        (touched.timeSlot && !updateValues.timeSlot) ||
        updateValues.timeSlot;

    return !!shouldUpdate;
};

export const getValidators = (translations: BookMeetingFormTranslations) => ({
    firstName: combineValidators([[isNotEmptyString, translations.fieldIsRequired]]),
    lastName: combineValidators([[isNotEmptyString, translations.fieldIsRequired]]),
    phoneNumber: combineValidators([[isNotEmptyString, translations.fieldIsRequired]]),
    email: combineValidators([
        [isNotEmptyString, translations.fieldIsRequired],
        [isEmail, translations.emailIsInvalidError],
    ]),
    isChecked: combineValidators([[isTrue, translations.fieldIsRequired]]),
    attendance: combineValidators([[isNotEmptyString, translations.fieldIsRequired]]),
    date: combineValidators([[isNotNull, translations.fieldIsRequired]]),
    timeSlot: combineValidators([[isNotEmptyString, translations.fieldIsRequired]]),
});

export const onSubmit = async (
    data: BookMeetingFormState,
    formikHelpers: FormikHelpers<BookMeetingFormState>,
    submitUrl: string,
    onSubmitted: () => void,
    genericError: string
) => {
    const {
        setStatus: formikSetStatus,
        setSubmitting: formikSetSubmitting,
        resetForm: formikResetForm,
    } = formikHelpers;
    formikSetStatus('');

    const request: BookMeetingRequest = {
        ...data,
        date: data.date ? formatISO(data.date, { representation: 'date' }) : '',
        sourceUrl: window.location.href,
    };
    const response = await makePostRequest(submitUrl, request);

    switch (response.state) {
        case 'Success':
            onSubmitted();
            formikSetStatus('');
            formikResetForm();
            break;
        case 'ClientError':
            formikSetStatus(response.message || genericError);
            break;

        default:
            formikSetStatus(genericError);
    }

    formikSetSubmitting(false);
};
